import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Card, Button } from '../../components/ui/Kit';
import Spinner from '../../components/ui/Spinner';
import EmptyState from '../../components/ui/EmptyState';
import { useToast } from '../../components/ui/Toast';
import { useAuth } from '../../contexts/AuthContext';
import { api } from '../../lib/api';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { bucketReminders, isReminderLeadership } from '../../lib/reminderUtils';
import { getActiveDirectoryUsers, useUsersDirectory } from '../../lib/usersDirectory';
import { ReminderFormModal, ReminderRow } from './ReminderFormModal';

const { FiBell, FiPlus } = FiIcons;

export default function Reminders() {
  const { user } = useAuth();
  const { addToast } = useToast();
  const { users: directoryUsers } = useUsersDirectory();
  const users = useMemo(() => getActiveDirectoryUsers(directoryUsers), [directoryUsers]);
  const [view, setView] = useState('mine');
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const reminders =
        view === 'delegated' ? await api.reminders.getCreatedByMe() : await api.reminders.getMine();
      const list = reminders || [];
      setRows(
        view === 'delegated'
          ? list.filter((r) => r.assigned_to !== user.id)
          : list,
      );
    } catch (err) {
      console.error(err);
      addToast({ type: 'error', title: 'Could not load reminders', message: err.message || 'Try again.' });
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, view, addToast]);

  useEffect(() => {
    document.title = 'Reminders — Prosperity Leads';
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const buckets = useMemo(() => bucketReminders(rows), [rows]);

  const save = async (payload) => {
    setSaving(true);
    try {
      if (editing?.id) {
        await api.reminders.update(editing.id, payload);
        addToast({ type: 'success', title: 'Reminder updated' });
      } else {
        await api.reminders.create({
          ...payload,
          lead_id: payload.lead_id ?? null,
        });
        addToast({ type: 'success', title: 'Reminder created' });
      }
      setFormOpen(false);
      setEditing(null);
      await load();
    } catch (err) {
      addToast({ type: 'error', title: 'Could not save', message: err.message || 'Try again.' });
    } finally {
      setSaving(false);
    }
  };

  const complete = async (row) => {
    setBusyId(row.id);
    try {
      await api.reminders.complete(row.id);
      addToast({ type: 'success', title: 'Reminder completed' });
      await load();
    } catch (err) {
      addToast({ type: 'error', title: 'Could not complete', message: err.message || 'Try again.' });
    } finally {
      setBusyId(null);
    }
  };

  const dismiss = async (row) => {
    setBusyId(row.id);
    try {
      await api.reminders.dismiss(row.id);
      addToast({ type: 'success', title: 'Reminder dismissed' });
      await load();
    } catch (err) {
      addToast({ type: 'error', title: 'Could not dismiss', message: err.message || 'Try again.' });
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (row) => {
    if (!window.confirm(`Delete reminder “${row.title}”?`)) return;
    setBusyId(row.id);
    try {
      await api.reminders.delete(row.id);
      addToast({ type: 'success', title: 'Reminder deleted' });
      await load();
    } catch (err) {
      addToast({ type: 'error', title: 'Could not delete', message: err.message || 'Try again.' });
    } finally {
      setBusyId(null);
    }
  };

  const renderBucket = (title, list, empty) => (
    <section className="space-y-2">
      <h2 className="text-[10.5px] font-heading font-bold uppercase tracking-[1.3px] text-primary">
        {title} ({list.length})
      </h2>
      {list.length === 0 ? (
        <p className="text-sm text-slateText px-1">{empty}</p>
      ) : (
        <div className="space-y-2">
          {list.map((row) => (
            <ReminderRow
              key={row.id}
              row={row}
              role={user?.role}
              viewerId={user?.id}
              onComplete={complete}
              onDismiss={dismiss}
              onEdit={(r) => {
                setEditing(r);
                setFormOpen(true);
              }}
              onDelete={remove}
              busyId={busyId}
            />
          ))}
        </div>
      )}
    </section>
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-2xl font-heading font-extrabold text-navy tracking-tight">
            Reminders
          </h1>
          <p className="text-slateText text-sm mt-1">
            Track follow-ups assigned to you{isReminderLeadership(user?.role) ? ', and anything you’ve delegated.' : '.'}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="inline-flex rounded-[100px] border border-lineGray bg-white p-0.5 text-xs font-semibold">
            <button
              type="button"
              onClick={() => setView('mine')}
              className={`rounded-[100px] px-3.5 py-1.5 ${view === 'mine' ? 'bg-navy text-white' : 'text-slateText'}`}
            >
              Assigned to me
            </button>
            <button
              type="button"
              onClick={() => setView('delegated')}
              className={`rounded-[100px] px-3.5 py-1.5 ${view === 'delegated' ? 'bg-navy text-white' : 'text-slateText'}`}
            >
              Created by me
            </button>
          </div>
          <Button
            size="sm"
            onClick={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          >
            <SafeIcon icon={FiPlus} className="h-3.5 w-3.5 mr-1" />
            New reminder
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-48 gap-3">
          <Spinner size="lg" className="text-primary" />
          <p className="text-slateText text-sm">Loading reminders…</p>
        </div>
      ) : rows.length === 0 ? (
        <Card className="p-2">
          <EmptyState
            icon={FiBell}
            title={view === 'delegated' ? 'Nothing delegated yet' : 'No reminders yet'}
            description={
              view === 'delegated'
                ? 'Reminders you create for other people will show up here.'
                : 'Create a reminder for yourself or assign one to a teammate.'
            }
            actionLabel="New reminder"
            onAction={() => {
              setEditing(null);
              setFormOpen(true);
            }}
          />
        </Card>
      ) : view === 'delegated' ? (
        <div className="space-y-2">
          {rows.map((row) => (
            <ReminderRow
              key={row.id}
              row={row}
              role={user?.role}
              viewerId={user?.id}
              onComplete={complete}
              onDismiss={dismiss}
              onEdit={(r) => {
                setEditing(r);
                setFormOpen(true);
              }}
              onDelete={remove}
              busyId={busyId}
            />
          ))}
        </div>
      ) : (
        <div className="space-y-6">
          {renderBucket('Overdue', buckets.overdue, 'None overdue.')}
          {renderBucket('Today', buckets.today, 'Nothing due today.')}
          {renderBucket('Upcoming', buckets.upcoming, 'No upcoming reminders.')}
          {renderBucket('Completed', buckets.completed, 'No completed or dismissed reminders.')}
        </div>
      )}

      <ReminderFormModal
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setEditing(null);
        }}
        onSave={save}
        saving={saving}
        users={users}
        currentUserId={user?.id}
        initial={editing}
        allowLeadPicker={false}
      />
    </div>
  );
}
